import { colorForCharacterFromMap } from "../../character/colors";
import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import {
  MARKER_DIAMETER,
  markerDiscAxisOffsetY,
} from "../../marker/MarkerMetrics/metrics";
import {
  paintPortraitFaces,
  portraitMorphAt,
  releasePortraitFaces,
} from "../../marker/PortraitMorph/morph";
import type { BoardSample, ChapterBoard } from "../ChapterBoard/types";
import {
  updateCharacterGroupDebugFrames,
  updateCharacterMarkerDebugFrames,
} from "../ApplyMarkers/debugFrames";
import { boardViewportXDelta } from "../BoardSample/viewportDelta";

const NAME_GAP_PX = 8;

export function applyBoardCharacterMarkers(
  root: HTMLElement,
  board: ChapterBoard,
  sample: BoardSample,
  layout: BookTimelineLayout,
  viewportWidth: number,
  boardModelDebug = false,
): void {
  const deltaX = boardViewportXDelta(
    viewportWidth,
    board.referenceViewportWidth,
  );
  const discRadius = MARKER_DIAMETER / 2;
  const axisOffset = markerDiscAxisOffsetY();
  const playheadMs = sample.playheadMs;

  for (const entry of sample.characters) {
    const pose = entry.pose;
    const parts = root.querySelectorAll<HTMLElement>(
      `[data-character-id="${entry.characterId}"]`,
    );
    if (parts.length === 0) {
      continue;
    }
    if (!pose.visible) {
      hideParts(parts);
      continue;
    }
    const centerX = pose.x + deltaX;
    const discTop = pose.y;
    const nameTop = discTop + discRadius * 2 + axisOffset + NAME_GAP_PX;
    for (const part of parts) {
      const below = part.dataset.markerPart === "name";
      const top = below ? nameTop : discTop;
      const opacity = below ? pose.nameOpacity : pose.fade ?? 1;
      placePart(part, centerX, top, opacity, pose.scale);
      if (!below) {
        part.style.zIndex = pose.behind ? "0" : "1";
      }
    }
    const cast = board.cast.find((c) => c.characterId === entry.characterId);
    if (cast === undefined) {
      continue;
    }
    const morph = portraitMorphAt(
      board.rail.stays,
      entry.characterId,
      cast.imageUrl,
      cast.appearances,
      playheadMs,
    );
    const fill = colorForCharacterFromMap(entry.characterId, layout.characterColors);
    const discs = root.querySelectorAll<HTMLElement>(
      `[data-marker-part="disc"][data-character-id="${entry.characterId}"] `
        + ".timeline-marker__disc",
    );
    for (const disc of discs) {
      paintPortraitFaces(disc, morph, fill);
    }
  }
  if (boardModelDebug) {
    updateCharacterMarkerDebugFrames(
      root,
      layout,
      sample.playheadMs,
      true,
    );
    updateCharacterGroupDebugFrames(
      root,
      board,
      layout,
      sample.playheadMs,
      true,
    );
  } else {
    updateCharacterMarkerDebugFrames(
      root,
      layout,
      sample.playheadMs,
      false,
    );
    updateCharacterGroupDebugFrames(
      root,
      board,
      layout,
      sample.playheadMs,
      false,
    );
  }
}

function hideParts(parts: NodeListOf<HTMLElement>): void {
  for (const part of parts) {
    // Not just invisible: a phone keeps memory for anything still laid out.
    if (part.style.display !== "none") {
      part.style.display = "none";
      releasePortraitFaces(part);
    }
  }
}

function placePart(
  part: HTMLElement,
  centerX: number,
  top: number,
  opacity: number,
  scale: number,
): void {
  part.style.opacity = String(opacity);
  part.style.display = "";
  part.style.visibility = "visible";
  part.style.setProperty("--marker-diameter", `${MARKER_DIAMETER}px`);
  part.style.transform =
    `translate(${centerX}px, ${top}px) translate(-50%, 0) scale(${scale})`;
}
